import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrders, useDebounce } from '@saiens/shared/hooks';
import { formatCurrency, formatDate } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Search, ChevronRight, ArrowLeft } from 'lucide-react';

function getDateDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const today = new Date().toISOString().slice(0, 10);
const sevenDaysAgo = getDateDaysAgo(7);

export function RecentPaymentsPage() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, error } = useOrders({
    payDateFrom: sevenDaysAgo,
    payDateTo: today,
    myOrders: true,
    search: debouncedSearch || undefined,
    limit: 50,
  });

  return (
    <div className="px-4 py-6">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <Link to="/" className="rounded-lg p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <h1 className="text-xl font-bold text-gray-900">近七日收款</h1>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜尋訂單編號或客戶..."
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
        />
      </div>

      {/* List */}
      <div className="rounded-xl bg-white shadow-sm">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : data?.items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">近七日無收款紀錄</div>
        ) : (
          <div className="divide-y">
            {data?.items.map((order) => (
              <Link
                key={order.id}
                to={`/sale/${order.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900">{order.name}</p>
                  <p className="truncate text-sm text-gray-500">
                    {order.partner_name || '未指定客戶'}
                    {order.project_name && ` • ${order.project_name}`}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                    {order.pay_date && (
                      <span className="text-green-600">
                        入帳 {formatDate(order.pay_date)}
                      </span>
                    )}
                    {order.user_name && <span>• {order.user_name}</span>}
                  </div>
                </div>
                <div className="ml-4 flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-800">
                      {formatCurrency(order.amount_total)}
                    </p>
                    {order.amount_invoiced > 0 && (
                      <p className="text-xs text-green-600">
                        已開帳 {formatCurrency(order.amount_invoiced)}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-300" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Total count */}
      {data && (
        <p className="mt-4 text-center text-sm text-gray-500">
          共 {data.total} 筆收款
        </p>
      )}
    </div>
  );
}
